import { useSelector } from "react-redux";
import { pbxIdSelector } from "@/react/features/customer/customerSlice";
import columnDefinition from "./columnDefinition";
import {
  handleUpdateResponse,
  useGetForwardingRulesExtensionsQuery,
  useUpdateForwardingRulesExtensionsMutation,
} from "../extensionsApi";
import { errorNotification, useErrorNotification } from "@/react/notifications";
import { useTable } from "@/react/components/table/useTable";
import schema from "./schema";
import React, { useCallback, useMemo } from "react";
import TableComponent from "@/react/components/table/TableComponent";
import omit from "lodash/omit";
import * as yup from "yup";
import { KEY } from "@/react/components/table/useTableMutations";

const columns = columnDefinition();

const ForwardingRulesTable = () => {
  const pbxId = useSelector(pbxIdSelector);
  const {
    data: extensions,
    isFetching,
    error,
    refetch,
  } = useGetForwardingRulesExtensionsQuery(pbxId);
  const [updateExtensions, { isLoading }] =
    useUpdateForwardingRulesExtensionsMutation();

  useErrorNotification(error);

  const idKey = yup.reach(schema, "extension.subscriptionId").spec.key;

  const { table, filters, state, mutations } = useTable(
    extensions,
    columns,
    schema,
    idKey
  );

  const {
    data,
    rowsToUpdate,
    resetData,

    setData,
    setRowsToUpdate,
  } = mutations;

  const isDirty = rowsToUpdate.size > 0;

  const onSave = useCallback(() => {
    if (rowsToUpdate.size > 0) {
      const fullRowsToUpdateData = [];
      rowsToUpdate.forEach((value, key) => {
        const { forwardingRules, extension } = data.find(
          (forwardingRule) => forwardingRule[KEY] === key
        );
        fullRowsToUpdateData.push({
          forwardingRules,
          subscriptionId: extension.subscriptionId,
        });
      });
      updateExtensions({
        pbxId,
        extensions: fullRowsToUpdateData,
      })
        .unwrap()
        .then(
          handleUpdateResponse({
            rowsToUpdate,
            setRowsToUpdate,
            setData,
            idKey,
          })
        )
        .catch(errorNotification);
    }
  }, [rowsToUpdate]);

  const isTableLoading = isFetching || isLoading;

  const finalMutations = useMemo(
    () => omit(mutations, ["deleteRows", "addRows"]),
    [mutations]
  );

  return (
    <TableComponent
      title="Forwarding Rules"
      schema={schema}
      isDirty={isDirty}
      isLoading={isTableLoading}
      onSave={onSave}
      onCancel={resetData}
      onRefresh={refetch}
      table={table}
      filters={filters}
      mutations={finalMutations}
      state={state}
    />
  );
};

export default ForwardingRulesTable;
